<template>
  <div class="status-insights-accordion" data-testid="status-insights-accordion">
    <div
      class="status-insights-accordion__header"
      :class="{ 'status-insights-accordion__header--expanded': isExpanded }"
      @click="toggleAccordion"
      data-testid="status-insights-accordion__header"
    >
      <div class="d-flex align-center gap-3">
        <div class="d-flex align-center">
          <img
            src="/img/icons/status-insight.svg"
            width="20"
            height="20"
            alt="Status Insights"
            class="status-insights-accordion__icon"
          />
          <span class="status-insights-accordion__title">Status Insights</span>
        </div>
        <!-- Local RU Approval Status -->
        <StatusIndicator
          v-if="hasApprovalData && workflows.islocalenabled"
          :variant="localApprovalStatusVariant"
          :customLabel="localApprovalStatusText"
          size="sm"
          outlined
          cornerRadius="8px"
        />
        <!-- Regional RU Approval Status -->
        <StatusIndicator
          v-if="hasApprovalData && workflows.isregionalenabled"
          :variant="regionalApprovalStatusVariant"
          :customLabel="regionalApprovalStatusText"
          size="sm"
          outlined
          cornerRadius="8px"
        />
      </div>
      <div class="d-flex align-center gap-3">
        <v-icon class="status-insights-accordion__chevron" :class="{ 'status-insights-accordion__chevron--rotated': isExpanded }">
          mdi-chevron-down
        </v-icon>
      </div>
    </div>

    <v-expand-transition>
      <div v-show="isExpanded" class="status-insights-accordion__body" data-testid="status-insights-accordion__body">
        <v-tabs
          v-model="activeTab"
          background-color="transparent"
          class="status-insights-accordion__tabs"
          data-testid="status-insights-accordion__tabs"
        >
          <v-tab data-testid="status-insights-accordion__tab-commentary-overview">
            Commentary Overview Status
          </v-tab>
          <v-tab data-testid="status-insights-accordion__tab-workflow-status">
            Workflow Status
          </v-tab>
          <v-tab data-testid="status-insights-accordion__tab-others">
            Others
          </v-tab>
        </v-tabs>
        <v-divider />
        <v-tabs-items v-model="activeTab" class="mt-3">
          <!-- Commentary Overview Status Tab -->
          <v-tab-item data-testid="status-insights-accordion__tab-content-commentary-overview">
            <RUStatusSummaryTable
              :localLabel="commentary_local_key_label"
              :regionalLabel="commentary_regional_key_label"
              :comparisons="['proforma', 'forecast']"
              :commentarysummarydata="mergedSummaryData"
              :loading="isLoading"
              data-testid="status-insights-accordion__status-summary-table"
            />
          </v-tab-item>

          <!-- Workflow Status Tab -->
          <v-tab-item data-testid="status-insights-accordion__tab-content-workflow-status">
            <WorkflowStatusTable
              :allworkflowstext="allworkflowstext"
              :workflowtext="workflowtext"
              :allworkflowskeys="allworkflowskeys"
              data-testid="status-insights-accordion__workflow-status-table"
            />
          </v-tab-item>

          <!-- Others Tab -->
          <v-tab-item data-testid="status-insights-accordion__tab-content-others">
            <div class="status-insights-accordion__others-table">
              <v-simple-table class="workflow-table">
                <thead class="tableHeaderBg">
                  <tr>
                    <th class="text-left">Features</th>
                    <th class="text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>SOD (Segregation of Duties)</td>
                    <td class="text-center">
                      <div class="d-flex align-center justify-center">
                        <StatusIndicator
                          :variant="isSodEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                          :custom-label="isSodEnabled ? 'Enabled' : 'Disabled'"
                          size="sm"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Bulk Approve</td>
                    <td class="text-center">
                      <div class="d-flex align-center justify-center">
                        <StatusIndicator
                          :variant="isBulkApproveEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                          :custom-label="isBulkApproveEnabled ? 'Enabled' : 'Disabled'"
                          size="sm"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Regional Level Auto Submission</td>
                    <td class="text-center">
                      <div class="d-flex align-center justify-center">
                        <StatusIndicator
                          :variant="isRegionalAutoSubmissionEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                          :custom-label="isRegionalAutoSubmissionEnabled ? 'Enabled' : 'Disabled'"
                          size="sm"
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import RUStatusSummaryTable from './RUStatusSummaryTable.vue';
import WorkflowStatusTable from './WorkflowStatusTable.vue';
import StatusIndicator from './StatusIndicator.vue';
import { STATUS_VARIANTS, STATUS_CODE_VARIANT_MAP, STATUS_CODE_VARIANT_MAP_REGIONAL, STATUS_CONFIG } from '../utils/statusConstants';

export default {
  name: 'StatusInsightsAccordion',
  components: {
    RUStatusSummaryTable,
    WorkflowStatusTable,
    StatusIndicator,
  },
  props: {
    /** @type {Object} Current commentary cycle data */
    commentarycycledata: { type: Object, default: () => ({}) },

    /** @type {Object} Commentary summary data from parent */
    commentarysummarydata: { type: Object, default: () => ({}) },

    /** @type {Object} Workflow configuration */
    workflows: {
      type: Object,
      default: () => ({
        islocalenabled: true,
        isregionalenabled: true,
        isworkflowsenabled: true,
      }),
    },

    /** @type {String} Label for local commentary */
    commentary_local_key_label: { type: String, default: 'Local' },

    /** @type {String} Label for regional commentary */
    commentary_regional_key_label: { type: String, default: 'Regional' },

    /** @type {String} Label for Reporting Unit */
    ruLabel: { type: String, default: 'RU' },

    /** @type {Array} All workflow text labels */
    allworkflowstext: { type: Array, default: () => [] },

    /** @type {Array} Current workflow text */
    workflowtext: { type: [String, Array], default: () => [] },

    /** @type {Array} All workflow keys */
    allworkflowskeys: { type: Array, default: () => [] },

    /** @type {Boolean} Whether SOD is enabled */
    isSodEnabled: { type: Boolean, default: true },

    /** @type {Boolean} Whether Bulk Approve is enabled */
    isBulkApproveEnabled: { type: Boolean, default: false },

    /** @type {Boolean} Whether Regional Auto Submission is enabled */
    isRegionalAutoSubmissionEnabled: { type: Boolean, default: false },
  },
  data() {
    return {
      STATUS_VARIANTS,
      isExpanded: false,
      activeTab: 0,
      localSummaryData: {},
      isLoading: false,
      hasFetchedOnce: false,
    };
  },
  computed: {
    mergedSummaryData() {
      return { ...this.commentarysummarydata, ...this.localSummaryData };
    },

    hasApprovalData() {
      return this.commentarycycledata && Object.keys(this.commentarycycledata).length > 0;
    },

    localApprovalStatusCode() {
      const code = this.commentarycycledata?.localRuLevelApprovalStatus;
      return [0, 1, 2, 3].includes(Number(code)) ? Number(code) : 0;
    },

    localApprovalStatusVariant() {
      if (!this.workflows.isworkflowsenabled) return STATUS_VARIANTS.DISABLED;
      return STATUS_CODE_VARIANT_MAP[this.localApprovalStatusCode] || STATUS_VARIANTS.DISABLED;
    },

    localApprovalStatusText() {
      if (!this.workflows.isworkflowsenabled) return 'Workflow Disabled';
      const label = STATUS_CONFIG[this.localApprovalStatusVariant]?.label || `${this.commentary_local_key_label} Status`;
      return `${this.commentary_local_key_label} - ${label}`;
    },

    regionalApprovalStatusCode() {
      const code = this.commentarycycledata?.regionalRuLevelApprovalStatus;
      return [0, 1, 2, 3].includes(Number(code)) ? Number(code) : 0;
    },

    regionalApprovalStatusVariant() {
      if (!this.workflows.isworkflowsenabled) return STATUS_VARIANTS.DISABLED;
      return STATUS_CODE_VARIANT_MAP_REGIONAL[this.regionalApprovalStatusCode] || STATUS_VARIANTS.DISABLED;
    },

    regionalApprovalStatusText() {
      if (!this.workflows.isworkflowsenabled) return 'Workflow Disabled';
      const label = STATUS_CONFIG[this.regionalApprovalStatusVariant]?.label || `${this.commentary_regional_key_label} Status`;
      return `${this.commentary_regional_key_label} - ${label}`;
    },

  },
  watch: {
    /**
     * When the cycle data changes (user switches RU), reset accordion state
     */
    'commentarycycledata._id'() {
      this.isExpanded = false;
      this.hasFetchedOnce = false;
      this.localSummaryData = {};
      this.activeTab = 0;
    },
  },
  methods: {
    toggleAccordion() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded && !this.hasFetchedOnce) {
        this.fetchSummaryData();
      }
    },

    async fetchSummaryData() {
      if (!this.commentarycycledata || !this.commentarycycledata._id) {
        return;
      }

      this.isLoading = true;

      const cycleId = this.commentarycycledata._id;
      const reportingunit =
        this.commentarycycledata.reportingunit && typeof this.commentarycycledata.reportingunit === 'object'
          ? this.commentarycycledata.reportingunit._id
          : this.commentarycycledata.reportingunit;
      const period = this.commentarycycledata.period;
      const cycletype = this.commentarycycledata.type;

      try {
        const result = await this.$store.dispatch('varianceanalysis/reportingUnit/getCycleSummaries', {
          cycleIds: [cycleId],
          reportingunit,
          period,
          cycletype,
          axiosInstance: this.axios,
        });

        if (result && result.length > 0) {
          this.localSummaryData = result[0];
        } else {
          console.warn('[StatusInsightsAccordion] No summary data returned');
        }
        this.hasFetchedOnce = true;
      } catch (e) {
        console.error('[StatusInsightsAccordion] Failed to fetch summary data', e);
      } finally {
        this.isLoading = false;
      }
    },
  },
  beforeDestroy() {
    this.localSummaryData = {};
    this.isExpanded = false;
    this.hasFetchedOnce = false;
  },
};
</script>

<style lang="scss" scoped>
@import './StatusInsightsAccordion.scss';
</style>
